import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  MessageSquare, Send, Loader2, Sparkles, User, X,
  CheckCircle2, XCircle, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIChatBox = ({ initialMessage, onClear }) => {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-open and send when initialMessage changes
  useEffect(() => {
    if (initialMessage) {
      setOpen(true);
      sendMessage(initialMessage);
      if (onClear) onClear();
    }
  }, [initialMessage]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;

    const userMsg = { role: 'user', text: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        { message: msg, session_id: sessionId },
        { headers }
      );
      if (res.data.error) {
        setMessages(prev => [...prev, { role: 'ai', text: `Error: ${res.data.error}`, ts: Date.now() }]);
      } else {
        const aiMsg = {
          role: 'ai',
          text: res.data.response,
          ts: Date.now(),
          actions: res.data.actions_taken || null,
        };
        setMessages(prev => [...prev, aiMsg]);

        // Notify on actions taken
        if (res.data.actions_taken?.length) {
          const succeeded = res.data.actions_taken.filter(a => a.success).length;
          const failed = res.data.actions_taken.filter(a => !a.success).length;
          if (succeeded > 0) toast.success(`${succeeded} action(s) completed`);
          if (failed > 0) toast.error(`${failed} action(s) failed`);
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: 'Sorry, I encountered an error. Please try again.',
        ts: Date.now(),
      }]);
      toast.error('AI chat failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'team_lead';

  const suggestions = isManager ? [
    "What does next week look like?",
    "Remove John's on-call shift on Friday",
    "Who's available to cover Thursday?",
    "Any scheduling conflicts this week?",
  ] : [
    "What does next week look like?",
    "Who's available to cover Friday?",
    "Any burnout risks I should know about?",
    "Can I approve another PTO request for next week?",
  ];

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-16 right-6 bg-indigo-600 text-white rounded-full p-3.5 shadow-lg hover:bg-indigo-500 transition-all hover:scale-105 z-50"
        data-testid="open-ai-chat"
      >
        <MessageSquare className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-16 right-6 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col z-50"
      style={{ maxHeight: '520px' }}
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 rounded-t-xl">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-500" />
          <span className="text-sm font-semibold text-slate-800">Shiftela AI</span>
          {isManager && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
              Actions Enabled
            </span>
          )}
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="close-ai-chat"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        style={{ minHeight: '200px', maxHeight: '350px' }}
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400 text-center">
              {isManager
                ? 'Ask questions or make schedule changes — I can take action'
                : 'Ask anything about your schedule, coverage, or staffing'}
            </p>
            <div className="space-y-1.5">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(s); sendMessage(s); }}
                  className="w-full text-left text-xs px-3 py-2 rounded-lg bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                  data-testid={`suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i}>
            <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'ai' && (
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="h-3 w-3 text-indigo-600" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-800'
                }`}
                data-testid={`chat-msg-${msg.role}-${i}`}
              >
                <div className="whitespace-pre-wrap leading-relaxed">{msg.text}</div>
              </div>
              {msg.role === 'user' && (
                <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="h-3 w-3 text-slate-600" />
                </div>
              )}
            </div>

            {/* Action results */}
            {msg.actions?.length > 0 && (
              <div className="ml-8 mt-1.5 space-y-1" data-testid={`actions-${i}`}>
                {msg.actions.map((act, j) => (
                  <div
                    key={j}
                    className={`flex items-start gap-1.5 text-xs px-2.5 py-1.5 rounded-md ${
                      act.success
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                    data-testid={`action-result-${j}`}
                  >
                    {act.success ? (
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    )}
                    <span>{act.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-3 w-3 text-indigo-600" />
            </div>
            <div className="bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
              <span className="text-xs text-slate-400">Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t border-slate-100">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isManager ? "Ask or instruct changes..." : "Ask about coverage, PTO, staffing..."}
            className="h-9 text-sm flex-1"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            size="sm"
            className="h-9 bg-indigo-600 hover:bg-indigo-500 px-3"
            data-testid="chat-send-btn"
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIChatBox;
